import { useFieldArray, UseFieldArrayMethods } from 'react-hook-form';
import { useCallback } from 'react';

import { MedicineSimple } from '@dreryk/drerykweb-medicine-api';

import { useTranslate } from '@dreryk/react-common';

import {
   DosageStageUnion,
   DosageStageValidation,
   ExtendedDosage,
   getDosageStageValidations,
   getExceededExtendedDosagePackagesCount,
   getExceededExtendedDosagePackagesCountByStage,
   hasDosageStageValidationError,
   hasMisplacedDosageBreak,
   hasZeroDoseCycle,
   isExtendedDosageExceedingYearLimit,
   normalizeStageForForm,
} from '~libs.shared.prescription';

import {
   createDosageBreak,
   createFrequencyDosageCycle,
   createTimesOfDayDosageCycle,
   DosageCycleClass,
   duplicateStage,
   getStageWithTimesOfDay,
   mapFormBeanToExtendedDosage,
} from '../extendedDosage.utils';
import { extendedDosageTemplatesTranslationKeys } from '../../../../../../translations';

import {
   ExtendedDosageFormBean,
   ExtendedDosageFormContext,
   ExtendedDosageFormController,
} from './ExtendedDosageForm.types';

type UseExtendedDosageStagesFieldArrayResult = UseFieldArrayMethods<DosageStageUnion> & {
   appendFrequencyCycle: VoidFunction;
   appendBreak: VoidFunction;
   duplicate: (index: number) => void;
   changeStageMode: (index: number, mode: DosageCycleClass) => void;
   getStages: () => DosageStageUnion[];
};

export const useExtendedDosageStagesFieldArray = (
   control: ExtendedDosageFormController['control'],
   getValues: ExtendedDosageFormController['getValues']
): UseExtendedDosageStagesFieldArrayResult => {
   const fieldArray = useFieldArray<DosageStageUnion>({
      name: 'stages',
      control: control,
   });

   const appendFrequencyCycle = useCallback(() => {
      fieldArray.append(createFrequencyDosageCycle());
   }, [fieldArray]);

   const appendBreak = useCallback(() => {
      fieldArray.append(createDosageBreak());
   }, [fieldArray]);

   const getStages = useCallback((): DosageStageUnion[] => {
      const values = getValues() as ExtendedDosageFormBean;

      return (values.stages ?? []).map((stage, index) =>
         getStageWithTimesOfDay({ ...stage, class: fieldArray.fields[index]?.class } as DosageStageUnion)
      );
   }, [getValues, fieldArray.fields]);

   const duplicate = useCallback(
      (index: number) => {
         const stages = getStages();
         const stage = stages[index];
         if (stage) {
            fieldArray.append(duplicateStage(stage));
         }
      },
      [fieldArray, getStages]
   );

   const changeStageMode = useCallback(
      (index: number, mode: DosageCycleClass) => {
         const next = mode === 'TimesOfDayDosageCycle' ? createTimesOfDayDosageCycle() : createFrequencyDosageCycle();
         fieldArray.remove(index);
         fieldArray.insert(index, next);
      },
      [fieldArray]
   );

   return {
      ...fieldArray,
      appendFrequencyCycle: appendFrequencyCycle,
      appendBreak: appendBreak,
      duplicate: duplicate,
      changeStageMode: changeStageMode,
      getStages: getStages,
   };
};

type UseExtendedDosageSubmitOptions = {
   handleSubmit: ExtendedDosageFormController['handleSubmit'];
   getStages: () => DosageStageUnion[];
   portionUnit: string | null;
   saveBlocked: boolean;
};

export const useExtendedDosageSubmit = (
   options: UseExtendedDosageSubmitOptions
): ExtendedDosageFormController['handleSubmit'] => {
   const { handleSubmit: submit, getStages, portionUnit, saveBlocked } = options;

   return useCallback<ExtendedDosageFormController['handleSubmit']>(
      (onValid, onInvalid) => async (event) => {
         await submit(async (bean, submitEvent) => {
            if (saveBlocked) {
               return;
            }

            await onValid({ ...bean, stages: getStages(), portionUnit: portionUnit }, submitEvent);
         }, onInvalid)(event);
      },
      [submit, getStages, portionUnit, saveBlocked]
   );
};

type UseZeroDoseCycleOptions = Pick<ExtendedDosageFormController, 'watch'> & {
   stageClasses: Pick<DosageStageUnion, 'class'>[];
};

export const useZeroDoseCycle = (options: UseZeroDoseCycleOptions): boolean => {
   const stages = options.watch('stages') ?? [];

   return hasZeroDoseCycle(
      options.stageClasses,
      stages.map((stage) =>
         ((stage as { portions?: { portion: number | null }[] }).portions ?? []).map((item) => item.portion)
      )
   );
};

type UseExceededPackagesCountOptions = Pick<ExtendedDosageFormController, 'watch'> & {
   getStages: () => DosageStageUnion[];
   portionUnit: string | null;
   medicine: MedicineSimple | undefined;
};

type ExceededPackages = {
   total: number | undefined;
   byStage: Map<number, number>;
};

const NO_EXCEEDED_PACKAGES: ExceededPackages = { total: undefined, byStage: new Map() };

export const useExceededPackages = (options: UseExceededPackagesCountOptions): ExceededPackages => {
   // Subscribes to stage changes, values are read through getStages to include the stage classes
   options.watch('stages');

   if (!options.medicine) {
      return NO_EXCEEDED_PACKAGES;
   }

   const extendedDosage = mapFormBeanToExtendedDosage({
      stages: options.getStages(),
      portionUnit: options.portionUnit,
      patientInstruction: null,
      templateName: null,
   });

   return {
      total: getExceededExtendedDosagePackagesCount(extendedDosage, options.medicine),
      byStage: getExceededExtendedDosagePackagesCountByStage(extendedDosage, options.medicine),
   };
};

type UseExtendedDosageValidationOptions = Pick<ExtendedDosageFormController, 'watch' | 'formState'> & {
   stagesFieldArray: UseExtendedDosageStagesFieldArrayResult;
   portionUnit: string | null;
   context: ExtendedDosageFormContext;
};

type ExtendedDosageValidation = {
   stageClasses: Pick<DosageStageUnion, 'class'>[];
   stagesEmpty: boolean;
   breakMisplaced: boolean;
   zeroDoseCycleVisible: boolean;
   treatmentDaysCountExceeded: boolean;
   exceededPackagesCount: number | undefined;
   stageValidations: DosageStageValidation[];
   saveBlocked: boolean;
};

export const useExtendedDosageValidation = (options: UseExtendedDosageValidationOptions): ExtendedDosageValidation => {
   const stageClasses = options.stagesFieldArray.fields.map((field) => ({ class: field.class! }));
   const zeroDoseCycle = useZeroDoseCycle({ watch: options.watch, stageClasses: stageClasses });
   const exceededPackages = useExceededPackages({
      watch: options.watch,
      getStages: options.stagesFieldArray.getStages,
      portionUnit: options.portionUnit,
      medicine: options.context.medicine,
   });

   const stagesEmpty = options.stagesFieldArray.fields.length === 0;
   const breakMisplaced = hasMisplacedDosageBreak(stageClasses);
   const stageValidations = getDosageStageValidations({
      stages: options.stagesFieldArray.getStages(),
      dosageFrequencyValidated: options.context.mode === 'TEMPLATE' || options.context.dosageFrequencyValidated,
      exceededPackagesCountByStage: exceededPackages.byStage,
   });
   const treatmentDaysCountExceeded =
      options.context.mode === 'PRESCRIPTION' &&
      isExtendedDosageExceedingYearLimit(
         mapFormBeanToExtendedDosage({
            stages: options.stagesFieldArray.getStages(),
            portionUnit: options.portionUnit,
            patientInstruction: null,
            templateName: null,
         })
      );

   return {
      stageClasses: stageClasses,
      stagesEmpty: stagesEmpty,
      breakMisplaced: breakMisplaced,
      zeroDoseCycleVisible: Boolean(options.formState.isSubmitted) && zeroDoseCycle,
      treatmentDaysCountExceeded: treatmentDaysCountExceeded,
      exceededPackagesCount: exceededPackages.total,
      stageValidations: stageValidations,
      saveBlocked:
         stagesEmpty ||
         breakMisplaced ||
         treatmentDaysCountExceeded ||
         exceededPackages.total !== undefined ||
         hasDosageStageValidationError(stageValidations),
   };
};

type UseExtendedDosageTemplateActionsOptions = Pick<
   ExtendedDosageFormController,
   'getValues' | 'reset' | 'markDirty'
> & {
   getStages: () => DosageStageUnion[];
   portionUnit: string | null;
};

type UseExtendedDosageTemplateActionsResult = {
   getTemplateExtendedDosage: () => ExtendedDosage;
   applyTemplateExtendedDosage: (extendedDosage: ExtendedDosage) => void;
};

export const useExtendedDosageTemplateActions = (
   options: UseExtendedDosageTemplateActionsOptions
): UseExtendedDosageTemplateActionsResult => {
   const { getValues, reset, markDirty, getStages, portionUnit } = options;

   const getTemplateExtendedDosage = useCallback(
      (): ExtendedDosage =>
         mapFormBeanToExtendedDosage({
            stages: getStages(),
            portionUnit: null,
            patientInstruction: null,
            templateName: null,
         }),
      [getStages]
   );

   const applyTemplateExtendedDosage = useCallback(
      (extendedDosage: ExtendedDosage): void => {
         reset({
            stages: extendedDosage.stages.map(normalizeStageForForm),
            portionUnit: portionUnit,
            patientInstruction: getValues().patientInstruction ?? null,
            templateName: null,
         });
         markDirty();
      },
      [getValues, reset, markDirty, portionUnit]
   );

   return {
      getTemplateExtendedDosage: getTemplateExtendedDosage,
      applyTemplateExtendedDosage: applyTemplateExtendedDosage,
   };
};

// A template is universal, so it does not store the unit and shows only a placeholder in its place
export const useDisplayedPortionUnit = (
   context: ExtendedDosageFormContext,
   portionUnit: string | null
): string | null => {
   const translate = useTranslate();

   return context.mode === 'TEMPLATE'
      ? translate(extendedDosageTemplatesTranslationKeys.portionUnitPlaceholder)
      : portionUnit;
};
